import { message } from 'antd';
import { useCallback, useEffect, useState } from 'react';

import { createSkillVersion, fetchSkillTemplateDetail, fetchSkillVersion } from '@/api/skills/api';
import { appRoutes, navigateTo } from '@/app/routes';

export type SkillEditorMode = 'new' | 'template' | 'version';
type SaveState = 'saved' | 'saving' | 'unsaved';

interface SkillEditorStateSnapshot {
  title: string;
  description: string;
  tags: string[];
  body: string;
  templateBundleMeta: {
    author: string | null;
    metaVersion: string | null;
  } | null;
}

const NEW_SKILL_BODY = '# New skill\n\n';

async function loadSkillEditorSnapshot(
  accessToken: string,
  mode: SkillEditorMode,
  templateKey?: string,
  versionId?: string,
): Promise<SkillEditorStateSnapshot> {
  if (mode === 'template' && templateKey) {
    const template = await fetchSkillTemplateDetail(accessToken, templateKey);
    return {
      title: template.title,
      description: template.description ?? '',
      tags: [...(template.tags ?? [])],
      body: template.body_markdown,
      templateBundleMeta: {
        author: template.author ?? null,
        metaVersion: template.meta_version ?? null,
      },
    };
  }

  if (mode === 'version' && versionId && versionId !== 'new') {
    const version = await fetchSkillVersion(accessToken, versionId);
    return {
      title: version.title,
      description: version.description ?? '',
      tags: [...(version.tags ?? [])],
      body: version.body_markdown,
      templateBundleMeta: null,
    };
  }

  return {
    title: 'New skill',
    description: '',
    tags: [],
    body: NEW_SKILL_BODY,
    templateBundleMeta: null,
  };
}

function buildSkillVersionRequest(
  mode: SkillEditorMode,
  templateKey: string | undefined,
  versionId: string | undefined,
  title: string,
  description: string,
  body: string,
  tags: string[],
) {
  const base = { title, description, body_markdown: body, tags };
  if (mode === 'template' && templateKey) {
    return { ...base, forked_from_template_key: templateKey, previous_version_id: null };
  }
  if (mode === 'version' && versionId && versionId !== 'new') {
    return { ...base, previous_version_id: versionId };
  }
  return base;
}

function getSkillSaveSuccessMessage(mode: SkillEditorMode): string {
  const labels: Record<SkillEditorMode, string> = {
    new: 'Skill created',
    template: 'Fork saved to the library',
    version: 'Saved new version',
  };
  return labels[mode];
}

export interface UseSkillEditorResult {
  loading: boolean;
  error: string | null;
  title: string;
  description: string;
  tags: string[];
  body: string;
  savedTitle: string;
  savedDescription: string;
  savedTags: string[];
  savedBody: string;
  saveState: SaveState;
  templateBundleMeta: { author: string | null; metaVersion: string | null } | null;
  setTitle: (value: string) => void;
  setDescription: (value: string) => void;
  setTags: (value: string[]) => void;
  setBody: (value: string) => void;
  setUnsavedIfNeeded: () => void;
  load: () => Promise<void>;
  /**
   * Saves the skill. `liveMarkdown` should be obtained from the editor ref
   * in the component at call time (e.g. `bodyEditorRef.current?.getMarkdown() ?? body`).
   */
  handleSave: (liveMarkdown: string) => Promise<void>;
}

/**
 * Orchestrates load and save operations for the skill editor.
 * The body editor ref and dirty-checking logic stay in the component since they
 * depend on the live editor DOM value.
 */
export function useSkillEditor(
  accessToken: string | null | undefined,
  mode: SkillEditorMode,
  templateKey?: string,
  versionId?: string,
): UseSkillEditorResult {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [body, setBody] = useState('');
  const [savedTitle, setSavedTitle] = useState('');
  const [savedDescription, setSavedDescription] = useState('');
  const [savedTags, setSavedTags] = useState<string[]>([]);
  const [savedBody, setSavedBody] = useState('');
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const [templateBundleMeta, setTemplateBundleMeta] = useState<{
    author: string | null;
    metaVersion: string | null;
  } | null>(null);

  const setUnsavedIfNeeded = useCallback((): void => {
    setSaveState((previous) => (previous === 'saving' ? previous : 'unsaved'));
  }, []);

  const applyLoadedSnapshot = useCallback((snapshot: SkillEditorStateSnapshot): void => {
    setTitle(snapshot.title);
    setDescription(snapshot.description);
    setTags(snapshot.tags);
    setBody(snapshot.body);
    setSavedTitle(snapshot.title);
    setSavedDescription(snapshot.description);
    setSavedTags([...snapshot.tags]);
    setSavedBody(snapshot.body);
    setSaveState('saved');
    setTemplateBundleMeta(snapshot.templateBundleMeta);
  }, []);

  const load = useCallback(async () => {
    if (!accessToken) {
      setLoading(false);
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const snapshot = await loadSkillEditorSnapshot(accessToken, mode, templateKey, versionId);
      applyLoadedSnapshot(snapshot);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load editor');
    } finally {
      setLoading(false);
    }
  }, [accessToken, mode, templateKey, versionId, applyLoadedSnapshot]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = useCallback(
    async (liveMarkdown: string): Promise<void> => {
      if (!accessToken) {
        void message.error('Sign in to save');
        return;
      }
      const trimmedTitle = title.trim();
      const trimmedBody = liveMarkdown.trim();
      const trimmedDescription = description.trim();
      if (!trimmedTitle || !trimmedBody) {
        void message.warning('Title and body are required');
        return;
      }
      setSaveState('saving');
      try {
        const request = buildSkillVersionRequest(
          mode,
          templateKey,
          versionId,
          trimmedTitle,
          trimmedDescription,
          trimmedBody,
          tags,
        );
        const created = await createSkillVersion(accessToken, request);
        void message.success(getSkillSaveSuccessMessage(mode));
        setSavedTitle(trimmedTitle);
        setSavedDescription(trimmedDescription);
        setSavedTags([...tags]);
        setSavedBody(trimmedBody);
        setSaveState('saved');
        navigateTo(appRoutes.skillEdit(created.id));
      } catch (e) {
        setSaveState('unsaved');
        void message.error(e instanceof Error ? e.message : 'Save failed');
      }
    },
    [accessToken, mode, templateKey, versionId, title, description, tags],
  );

  return {
    loading,
    error,
    title,
    description,
    tags,
    body,
    savedTitle,
    savedDescription,
    savedTags,
    savedBody,
    saveState,
    templateBundleMeta,
    setTitle,
    setDescription,
    setTags,
    setBody,
    setUnsavedIfNeeded,
    load,
    handleSave,
  };
}
